import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Copy, Clock, FileText, Layers, Check } from 'lucide-react';
import { transcriptionService } from '../services/transcriptionService';

const TranscriptViewer = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [transcriptData, setTranscriptData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('combined'); // 'combined' or 'chunks'
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (jobId) {
      fetchTranscriptData();
    }
  }, [jobId]);

  const fetchTranscriptData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching transcript data for jobId:', jobId);
      const result = await transcriptionService.getTranscriptionChunks(jobId);
      console.log('Received transcript data:', result);
      
      // Ensure the result is valid
      if (result && typeof result === 'object') {
        setTranscriptData(result);
      } else {
        throw new Error('Invalid transcript data received');
      }
    } catch (err) {
      console.error('Error fetching transcript data:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!transcriptData) return;
    
    let textToCopy = '';
    if (transcriptData.chunks && Array.isArray(transcriptData.chunks)) {
      textToCopy = transcriptData.chunks
        .map(chunk => chunk.full_transcript_text || chunk.transcript || '')
        .join('\n\n');
    } else if (transcriptData.records && Array.isArray(transcriptData.records)) {
      textToCopy = transcriptData.records
        .map(record => record.full_transcript_text || record.transcript || '')
        .join('\n\n');
    }

    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const downloadTranscript = () => {
    if (!transcriptData) return;

    let content = '';
    const chunks = transcriptData.chunks || transcriptData.records || [];
    
    if (viewMode === 'combined') {
      content = `Transcript - Job ID: ${transcriptData.job_id || jobId}\n`;
      content += `Status: ${transcriptData.status || 'Completed'}\n`;
      content += `Generated: ${new Date().toLocaleString()}\n`;
      content += '\n' + '='.repeat(50) + '\n\n';
      
      content += chunks
        .map(chunk => chunk.full_transcript_text || chunk.transcript || '')
        .join('\n\n');
    } else {
      content = `Transcript Chunks - Job ID: ${transcriptData.job_id || jobId}\n`;
      content += `Status: ${transcriptData.status || 'Completed'}\n`;
      content += `Total Chunks: ${chunks.length}\n`;
      content += `Generated: ${new Date().toLocaleString()}\n`;
      content += '\n' + '='.repeat(50) + '\n\n';
      
      chunks.forEach((chunk, index) => {
        content += `CHUNK ${chunk.chunk_index || index + 1}\n`;
        content += `File: ${chunk.file_name || `chunk_${index + 1}`}\n`;
        content += '-'.repeat(30) + '\n';
        content += (chunk.full_transcript_text || chunk.transcript || 'No transcript available') + '\n\n';
      });
    }

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript_${jobId}_${viewMode}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const calculateStats = () => {
    if (!transcriptData) return {};
    
    const chunks = transcriptData.chunks || transcriptData.records || [];
    const totalChunks = chunks.length;
    const totalChars = chunks.reduce((sum, chunk) => {
      const text = chunk.full_transcript_text || chunk.transcript || '';
      return sum + text.length;
    }, 0);
    const totalWords = chunks.reduce((sum, chunk) => {
      const text = chunk.full_transcript_text || chunk.transcript || '';
      return sum + text.split(/\s+/).filter(word => word.length > 0).length;
    }, 0);
    
    // Estimate processing time (this would typically come from the API)
    const estimatedMinutes = Math.ceil(totalChunks * 0.5); // Rough estimate
    
    return {
      totalChunks,
      totalChars,
      totalWords,
      estimatedMinutes
    };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <div className="container" style={{ padding: '32px 0' }}>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '400px',
          color: '#64748b'
        }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '3px solid #e2e8f0',
            borderTop: '3px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '16px'
          }}></div>
          <p>Loading transcript...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ padding: '32px 0' }}>
        <div className="error-container">
          <div className="error-title">Failed to Load Transcript</div>
          <div className="error-message">
            {error.message || 'An error occurred while loading the transcript.'}
          </div>
          <button 
            onClick={() => navigate('/transcriptions')}
            className="btn btn-primary"
            style={{ marginTop: '16px' }}
          >
            <ArrowLeft style={{ width: '16px', height: '16px' }} />
            Back to Transcriptions
          </button>
        </div>
      </div>
    );
  }

  const chunks = (() => {
    if (!transcriptData) return [];
    
    const rawChunks = transcriptData.chunks || transcriptData.records || [];
    
    // Ensure chunks is always an array and filter out invalid entries
    if (Array.isArray(rawChunks)) {
      return rawChunks.filter(chunk => chunk && typeof chunk === 'object');
    }
    
    return [];
  })();

  return (
    <div className="container" style={{ padding: '32px 0' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        marginBottom: '32px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <button 
            onClick={() => navigate('/transcriptions')}
            className="btn btn-secondary"
            style={{ marginBottom: '16px' }}
          >
            <ArrowLeft style={{ width: '16px', height: '16px' }} />
            Back to Transcriptions
          </button>
          
          <h1 style={{ 
            fontSize: '28px', 
            fontWeight: '700', 
            color: '#1e293b',
            marginBottom: '8px'
          }}>
            Full Transcript View
          </h1>
          
          <div style={{ 
            color: '#64748b', 
            fontSize: '14px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '24px',
            alignItems: 'center'
          }}>
            <div>
              <strong>Job ID:</strong> {String(transcriptData?.job_id || jobId)}
            </div>
            <div>
              <strong>Status:</strong> {String(transcriptData?.status || 'Completed')}
            </div>
            <div>
              <strong>Chunks:</strong> {String(chunks.length)}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            onClick={copyToClipboard}
            className="btn btn-secondary"
            disabled={!transcriptData}
          >
            {copied ? (
              <>
                <Check style={{ width: '16px', height: '16px' }} />
                Copied!
              </>
            ) : (
              <>
                <Copy style={{ width: '16px', height: '16px' }} />
                Copy All
              </>
            )}
          </button>
          
          <button
            onClick={downloadTranscript}
            className="btn btn-primary"
            disabled={!transcriptData}
          >
            <Download style={{ width: '16px', height: '16px' }} />
            Download
          </button>
        </div>
      </div>

      {/* View Mode Toggle */}
      <div style={{ 
        display: 'flex', 
        gap: '8px', 
        marginBottom: '32px',
        padding: '4px',
        background: '#f1f5f9',
        borderRadius: '8px',
        width: 'fit-content'
      }}>
        <button
          onClick={() => setViewMode('combined')}
          style={{
            padding: '8px 16px',
            borderRadius: '6px',
            border: 'none',
            background: viewMode === 'combined' ? '#3b82f6' : 'transparent',
            color: viewMode === 'combined' ? 'white' : '#64748b',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <FileText style={{ width: '14px', height: '14px' }} />
          Combined View
        </button>
        
        <button
          onClick={() => setViewMode('chunks')}
          style={{
            padding: '8px 16px',
            borderRadius: '6px',
            border: 'none',
            background: viewMode === 'chunks' ? '#3b82f6' : 'transparent',
            color: viewMode === 'chunks' ? 'white' : '#64748b',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <Layers style={{ width: '14px', height: '14px' }} />
          Chunks View
        </button>
      </div>

      {/* Content */}
      <div className="card" style={{ padding: '32px', marginBottom: '32px' }}>
        {viewMode === 'combined' ? (
          // Combined View
          <div>
            <h3 style={{ 
              fontSize: '18px', 
              fontWeight: '600', 
              color: '#1e293b',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <FileText style={{ width: '18px', height: '18px' }} />
              Complete Transcript
            </h3>
            
            <div style={{
              fontSize: '16px',
              lineHeight: '1.8',
              color: '#374151',
              textAlign: 'justify',
              whiteSpace: 'pre-wrap'
            }}>
              {chunks
                .map(chunk => {
                  const text = chunk.full_transcript_text || chunk.transcript || '';
                  return typeof text === 'string' ? text : String(text);
                })
                .filter(text => text.trim())
                .join('\n\n')}
            </div>
          </div>
        ) : (
          // Chunks View
          <div>
            <h3 style={{ 
              fontSize: '18px', 
              fontWeight: '600', 
              color: '#1e293b',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Layers style={{ width: '18px', height: '18px' }} />
              Individual Chunks
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {chunks.map((chunk, index) => (
                <div key={index} style={{
                  padding: '20px',
                  background: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px',
                    paddingBottom: '8px',
                    borderBottom: '1px solid #e2e8f0'
                  }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#1e293b'
                    }}>
                      📁 {String(chunk.file_name || `Chunk ${chunk.chunk_index || index + 1}`)}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#64748b',
                      background: 'white',
                      padding: '4px 8px',
                      borderRadius: '4px'
                    }}>
                                              {String((chunk.full_transcript_text || chunk.transcript || '').length)} chars
                    </div>
                  </div>
                  
                  <div style={{
                    fontSize: '15px',
                    lineHeight: '1.7',
                    color: '#374151',
                    textAlign: 'justify'
                  }}>
                    {(() => {
                      const text = chunk.full_transcript_text || chunk.transcript || 'No transcript available for this chunk';
                      return typeof text === 'string' ? text : String(text);
                    })()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Statistics */}
      <div className="card" style={{ padding: '24px' }}>
        <h3 style={{ 
          fontSize: '18px', 
          fontWeight: '600', 
          color: '#1e293b',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <Clock style={{ width: '18px', height: '18px' }} />
          Processing Statistics
        </h3>
        
        <div className="grid grid-cols-4" style={{ gap: '24px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              fontSize: '28px', 
              fontWeight: '700', 
              color: '#3b82f6',
              marginBottom: '4px'
            }}>
              {stats.estimatedMinutes}
            </div>
            <div style={{ fontSize: '14px', color: '#64748b' }}>
              Minutes Processed
            </div>
          </div>
          
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              fontSize: '28px', 
              fontWeight: '700', 
              color: '#10b981',
              marginBottom: '4px'
            }}>
              {stats.totalChunks}
            </div>
            <div style={{ fontSize: '14px', color: '#64748b' }}>
              Audio Chunks
            </div>
          </div>
          
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              fontSize: '28px', 
              fontWeight: '700', 
              color: '#f59e0b',
              marginBottom: '4px'
            }}>
              {stats.totalWords.toLocaleString()}
            </div>
            <div style={{ fontSize: '14px', color: '#64748b' }}>
              Total Words
            </div>
          </div>
          
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              fontSize: '28px', 
              fontWeight: '700', 
              color: '#ef4444',
              marginBottom: '4px'
            }}>
              {stats.totalChars.toLocaleString()}
            </div>
            <div style={{ fontSize: '14px', color: '#64748b' }}>
              Characters
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TranscriptViewer; 